import { css, type CSSObject } from '@emotion/react'
import {
  Color,
  CornerRadius,
  OppositeTheme,
  Spacing,
  ZIndex,
} from '@opallabs/gem'
import { WithRequiredTheme } from '@opallabs/lapidary'
import { ControlBarStyleProps } from './types'

type ContainerStyleProps = WithRequiredTheme

export const controlBar = ({
  isVisible,
  theme,
}: ControlBarStyleProps & ContainerStyleProps): CSSObject => {
  const base: CSSObject = {
    position: 'sticky',
    insetBlockEnd: -60,
    overflow: 'hidden',
    blockSize: 'auto',
    maxBlockSize: 0,
    opacity: 0,
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    alignItems: 'center',
    borderBlockStart: '1px solid transparent',
    borderStartStartRadius: '0',
    borderStartEndRadius: '0',
    borderEndEndRadius: '12px',
    borderEndStartRadius: '12px',
    pointerEvents: 'none',
    transition: [
      'max-height 0.15s ease-in-out',
      'opacity 0.15s ease-in-out',
      'border-top-color 0.15s ease-in-out',
      'padding 0.15s ease-in-out',
    ].join(','),
    zIndex: ZIndex.Element.Level1,

    '> div': {
      paddingInline: '16px',
      transition: 'transform 0.15s ease-in-out',
    },
  }

  const visibleStyle: CSSObject = {
    maxBlockSize: '57px',
    opacity: 1,
    pointerEvents: 'auto',
  }

  return {
    ...base,
    ...(isVisible ? visibleStyle : {}),
    borderBlockStartColor: isVisible
      ? Color[theme].Surface.N300
      : 'transparent',
  }
}

export const group = ({ theme }: WithRequiredTheme): CSSObject => ({
  paddingInlineEnd: '4px',
  marginInlineEnd: '4px',
  borderInlineEnd: '1px solid transparent',
  display: 'inline-flex',

  borderInlineEndColor: Color[theme].Surface.N400,

  '&:last-of-type': {
    borderInlineEnd: 0,
    marginInlineEnd: 0,
    paddingInlineEnd: 0,
  },
})

export const button = ({ theme }: WithRequiredTheme): CSSObject => ({
  backgroundColor: 'transparent',
  border: 0,
  borderRadius: CornerRadius.Small,
  blockSize: '26px',
  inlineSize: '26px',
  marginBlock: '0',
  marginInline: '2px',
  padding: 0,
  lineHeight: 1,
  display: 'inline-flex',
  justifyContent: 'center',
  alignItems: 'center',

  '&:not(:disabled):not([aria-disabled="true"])': {
    cursor: 'pointer',
  },
  '&:disabled, &[aria-disabled="true"]': {
    cursor: 'not-allowed',
    opacity: 0.6,
  },

  '&[aria-pressed="true"]': {
    backgroundColor: Color[OppositeTheme[theme]].Surface.A100,
    color: Color[OppositeTheme[theme]].Text.Default,
  },
  '&:not([aria-pressed="true"])': {
    backgroundColor: Color[theme].Surface.N000,
    color: Color[theme].Text.Default,
  },
  '&:hover&:not([aria-pressed="true"])&:not(:disabled)&:not([aria-disabled="true"])':
    {
      backgroundColor: Color[theme].Surface.A100,
    },

  svg: {
    textAlign: 'center',
    verticalAlign: 'middle',

    path: {
      fill: 'currentColor',
    },
  },
})

export const buttonGroups: CSSObject = {
  paddingBlock: '14px',
}

export const saveArea = ({ theme }: WithRequiredTheme): CSSObject => ({
  display: 'flex',
  alignItems: 'center',
  gap: '15px',
  color: Color[theme].Text.Gray,
})

export const fullScreenClose: CSSObject = {
  inlineSize: '32px',
  blockSize: '32px',
  position: 'fixed',
  insetBlockStart: '64px',
  insetInlineEnd: '24px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: 'none',
  border: 0,
  padding: 0,
  cursor: 'pointer',
  borderRadius: '6px',

  '&:hover': {
    backgroundColor: '#2a70ea14',
  },
}

export const colorPopoverButtonWrapper = css({
  display: 'contents',

  // Show active color as a faux underline in the button
  '& button': {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      insetBlockEnd: Spacing.XS,
      insetInline: Spacing.XS * 1.5,
      blockSize: Spacing.XS / 2,
      backgroundColor: 'var(--active-color, transparent)',
    },
  },
})

export const colorPopoverContent = css({
  '.color-radio-group': {
    display: 'grid',
    gap: Spacing.S,
    gridTemplateColumns: 'repeat(4, 1fr)',
    padding: Spacing.S - Spacing.XS,
  },
})

export const controlButtonWithPopoverButton = css({
  '& > .lapidary-icon': {
    margin: 0,
    paddingInlineStart: 4,
    color: Color.light['Icons/Secondary'],
  },
})

export const controlButtonWithPopoverContent = (fixedPopoverWidth: boolean) =>
  css({
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    inlineSize: fixedPopoverWidth ? 124 : 'max-content',
    justifyContent: 'center',
    padding: 6,
  })
